import { Button } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import type { DiffLineView, DiffSideView, Translate } from './content-types'

/**
 * Props for {@link DiffView}.
 *
 * @property mode - `inline` (one column with markers) or `side` (two columns).
 * @property title - The title change, when the titles differ.
 * @property error - A translated refusal instead of the diff.
 */
export interface DiffViewProps {
	t: Translate
	fromLabel: string
	toLabel: string
	mode: 'inline' | 'side'
	title: { before: string; after: string } | null
	inline: DiffLineView[]
	side: DiffSideView[]
	error: string | null
	onModeChange: (mode: 'inline' | 'side') => void
	onClose: () => void
}

const TONE: Record<DiffLineView['op'], string> = {
	equal: 'text-ds-text',
	insert: 'bg-ds-success/10 text-ds-text',
	delete: 'bg-ds-danger/10 text-ds-text',
}

function DiffBody({ props }: { props: DiffViewProps }): ReactElement {
	return (
		<>
			{props.mode === 'inline' ? (
				<pre className="flex flex-col font-mono text-ds-sm" data-testid="diff-inline">
					{props.inline.map((row, i) => (
						// Lines repeat, so the index is the only stable identity.
						// biome-ignore lint/suspicious/noArrayIndexKey: positional diff rows
						<span key={i} className={TONE[row.op]}>{`${row.marker}${row.line}`}</span>
					))}
				</pre>
			) : (
				<table className="w-full font-mono text-ds-sm" data-testid="diff-side">
					<tbody>
						{props.side.map((row, i) => (
							// biome-ignore lint/suspicious/noArrayIndexKey: positional diff rows
							<tr key={i}>
								<td className={row.left === null ? '' : TONE[row.op]}>{row.left ?? ''}</td>
								<td className={row.right === null ? '' : TONE[row.op]}>{row.right ?? ''}</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
		</>
	)
}

/** A line diff between two versions of one language, inline or side by side (organism). */
export function DiffView(props: DiffViewProps): ReactElement {
	const { t } = props
	return (
		<section className="flex flex-col gap-3" aria-label={t('diff.title')}>
			<header className="flex flex-wrap items-center gap-2">
				<h2 className="text-ds-lg font-semibold text-ds-text">
					{t('diff.heading', { from: props.fromLabel, to: props.toLabel })}
				</h2>
				<Button
					variant={props.mode === 'inline' ? 'primary' : 'muted'}
					size="sm"
					onPress={() => props.onModeChange('inline')}
				>
					{t('diff.inline')}
				</Button>
				<Button
					variant={props.mode === 'side' ? 'primary' : 'muted'}
					size="sm"
					onPress={() => props.onModeChange('side')}
				>
					{t('diff.side')}
				</Button>
				<Button variant="muted" size="sm" onPress={props.onClose}>
					{t('actions.close')}
				</Button>
			</header>
			{props.error ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{props.error}
				</p>
			) : null}
			{props.title ? (
				<p className="text-ds-sm">{t('diff.titleChange', { before: props.title.before, after: props.title.after })}</p>
			) : null}
			<DiffBody props={props} />
		</section>
	)
}
